import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Row } from "react-bootstrap";
import dateformat from 'dateformat';
import cx from "classnames";
import { flatMap, get, map, size, times } from "lodash";
import { useSelector } from "react-redux";
import { userDetailsHeader } from "src/utils/experiences.helper";
import { toastFailure, toastSuccess } from "../../utils/notifications";
import TableHeader from "./components/tableHeader";
import TableCell from "./components/tableCell";
import SaveConfirmation from "../saveConfirmation";
import PlaceholderCell from "./components/placeholderCell";
import InfiniteScroll from "src/components/infiniteScroll";
import Slider from "src/components/slider";
import Button from "src/components/button";
import Text from "src/components/text";
import { ShellContext } from "../..";
import { DropDown } from "../../../components/dropdown";
import {
  useStoryboardUsersQuery,
  useRemoveStoryboardUserAccessMutation,
  useUpdateStoryboardUserAccessMutation,
} from "src/query/experiences/storyboardSettings/permissions";
import { PendingRoleChange } from "src/types/experiences/storyboardSettings";
import { Avatar } from "./components/avatar";
import InviteUsersDialog from "../inviteUsers";
import styles from "./rolesAndPermissions.module.scss";

interface StoryboardUserRecord {
  id: string;
  role: string;
  createdDate?: number;
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    lastLoginDate?: number;
    lastActivityDate?: number;
    icon?: { thumbnail?: string } | null;
  };
}

const ROLE_OPTIONS = [
  { key: "owner", label: "Owner" },
  { key: "editor", label: "Editor" },
  { key: "viewer", label: "Viewer" },
];

const emptyUsersImg = "/images/dealInsights/emptyDeals.svg";

const NoResultsFound = ({ msg, src }: { msg: string; src: string }) => (
  <div className={styles.emptyState}>
    <img src={src} alt="No results" className={styles.emptyStateImg} />
    <p className={styles.emptyStateMsg}>{msg}</p>
  </div>
);

const RowPlaceholder = () => (
  <Row className={styles.tableRow}>
    {map(userDetailsHeader, (header) => (
      <PlaceholderCell key={header.label} col={header.width} type={header.value} />
    ))}
  </Row>
);

const UserRow = ({
  item,
  onDelete,
  onRoleChangeRequest,
  pendingRoleChanges,
  confirmedRoles
}: {
  item: StoryboardUserRecord;
  onDelete: (item: StoryboardUserRecord) => void;
  pendingRoleChanges: Record<string, PendingRoleChange>;
  onRoleChangeRequest: (userAccessId: string, oldRole: string, newRole: string) => void;
  confirmedRoles: Record<string, string>;
}) => {
  const { user, role } = item;
  const currentUser = useSelector((state: any) => get(state, "users.me"));
  const isSelf = currentUser?.id === user?.id;

  const fullName =
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || "—";

    const handleRoleChange = (key: string) => {
      if (isSelf) return;
      onRoleChangeRequest(item.id, role, key);
    };

  const displayedRole =
    pendingRoleChanges[item.id]?.newRole
    ?? confirmedRoles[item.id]
    ?? role;

  return (
    <Row className={cx(styles.tableRow)}>
      {/* Name + avatar */}
      <TableCell
        col={userDetailsHeader[0].width}
        type="name"
        value={
          <div className={styles.nameWithAvatar}>
            <Avatar user={user} />
            <span>{fullName}</span>
          </div>
        }
      />
      {/* Email */}
      <TableCell
        col={userDetailsHeader[1].width}
        type="name"
        fontSize="12px"
        value={user.email ?? "—"}
      />
      {/* Role — always visible, pointer-events off for self */}
      <TableCell
        col={userDetailsHeader[2].width}
        type="name"
        fontSize="12px"
        value={
          <div className={cx({ [styles.disabledAction]: isSelf })}>
            <DropDown
              selected={{
                key: displayedRole,
                label: ROLE_OPTIONS.find((o) => o.key === displayedRole)?.label ?? "",
              }}
              options={ROLE_OPTIONS}
              onChange={handleRoleChange}
            />
          </div>
        }
      />
      {/* Last Active */}
      <TableCell
        col={userDetailsHeader[3].width}
        type="name"
        fontSize="12px"
        value={dateformat(currentUser.lastLoginDate, "dd mmm yyyy")}
      />
      {/* Remove — always visible, disabled for self */}
      <TableCell
        col={userDetailsHeader[4].width}
        type="name"
        fontSize="12px"
        value={
          <button
            className={cx(styles.delete)}
            disabled={isSelf}
            onClick={() => !isSelf && onDelete(item)}
          >
            <i className="paperflite-delete" />
            Remove
          </button>
        }
      />
    </Row>
  );
};

const RolesAndPermissions: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceId, storyboardId } = useParams<{ storyboardId: string, workspaceId: string}>();

  const [pendingRoleChanges, setPendingRoleChanges] = useState<Record<string, PendingRoleChange>>({});
  const { setHideActionBar, registerUploadHandler } = useContext(ShellContext);

  const [userToDelete, setUserToDelete] = useState<StoryboardUserRecord | null>(null);
  const [confirmedRoles, setConfirmedRoles] = useState<Record<string, string>>({});
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const { data, isLoading, isFetching, hasNextPage, loadMore } =
  useStoryboardUsersQuery(storyboardId ?? "", { sort: "createdDate", order: "desc" } as any);

  const { mutate: removeAccess, isPending: isRemoving } =
    useRemoveStoryboardUserAccessMutation();

  const users: StoryboardUserRecord[] = useMemo(
    () => flatMap(data?.pages ?? [], (page) => page.records),
    [data],
  );

  const { mutate: updateAccess, isPending: isUpdatingRole } =
  useUpdateStoryboardUserAccessMutation();

  useEffect(() => {
    return () => {
      setPendingRoleChanges({});
      setHideActionBar(false);
    };
  }, []);

  useEffect(() => {
    if (!location.state?.openInvite) return;

    const timer = setTimeout(() => {
      setShowInviteDialog(true);
      navigate(location.pathname, { replace: true, state: {} });
    }, 500);

    return () => clearTimeout(timer);
  }, [location.state?.openInvite]);

  useEffect(() => {
    registerUploadHandler(() => setShowInviteDialog(true));
    return () => registerUploadHandler(null);
  }, [registerUploadHandler]);

  useEffect(() => {
    if (Object.keys(confirmedRoles).length === 0) return;
    setConfirmedRoles({});
  }, [users]);

  const handleRoleChangeRequest = (
    userAccessId: string,
    oldRole: string,
    newRole: string,
  ) => {
    setPendingRoleChanges((prev) => {
      const next = { ...prev };
  
      if (newRole === (confirmedRoles[userAccessId] ?? oldRole)) {
        delete next[userAccessId];
      } else {
        next[userAccessId] = { userAccessId, oldRole, newRole };
      }
  
      const hasPending = Object.keys(next).length > 0;
      setHideActionBar(hasPending);
      return next;
    });
  };

  const handleConfirmRoleChange = () => {
    if (!Object.keys(pendingRoleChanges).length) return;
    const payload = Object.values(pendingRoleChanges).map((p) => ({
      user: p.userAccessId,
      role: p.newRole,
    }));
  
    updateAccess(
      { storyboardId: storyboardId ?? "", data: payload },
      {
        onSuccess: () => {
          setConfirmedRoles((prev) => {
            const next = { ...prev };
            Object.values(pendingRoleChanges).forEach((p) => {
              next[p.userAccessId] = p.newRole;
            });
            return next;
          });
          setPendingRoleChanges({});
          setHideActionBar(false);
          toastSuccess("User permissions updated successfully");
        },
        onError: () => {
          toastFailure("Failed to update user roles");
        },
      },
    );
  };

  const handleCancelRoleChange = () => {
    if (isUpdatingRole) return;
    setPendingRoleChanges({});
    setHideActionBar(false);
  };
  const handleConfirmDelete = () => {
    if (!userToDelete) return;
    removeAccess(userToDelete.id, {
      onSuccess: () => {
        setUserToDelete(null);
        toastSuccess("Removed User from storyboard");
      },
      onError: () => {
        toastFailure("Failed to remove user");
      },
    });
  };

  const deleteeFullName =
    [userToDelete?.user.firstName, userToDelete?.user.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "";

  return (
    <div className={styles.container}>
      {Object.keys(pendingRoleChanges).length > 0 && (
        <div className={styles.saveConfirmationWrapper}>
          <SaveConfirmation
            show
            message={`${Object.keys(pendingRoleChanges).length} Permission access will be changed`}
            isSaving={isUpdatingRole}
            confirmText="Confirm"
            loadingText="Confirming..."
            cancelText="Cancel"
            onSave={handleConfirmRoleChange}
            onCancel={handleCancelRoleChange}
          />
        </div>
      )}
      <div className={styles.tableWrapper}>
        <Row className={cx(styles.tableHeaderRow)}>
          {map(userDetailsHeader, (header) => (
            <TableHeader key={header.label} col={header.width} value={header.label} onClick={() => {}} />
          ))}
        </Row>

        <div className={styles.tableBody}>
          {isLoading ? (
            <div className={styles.tableBodyWrapper}>
              {times(4, (index) => <RowPlaceholder key={index} />)}
            </div>
          ) : size(users) > 0 ? (
            <InfiniteScroll
              loading={isFetching}
              hasMore={hasNextPage ?? false}
              loadMore={loadMore}
              loader={() => <><RowPlaceholder /><RowPlaceholder /></>}
              className={styles.tableBodyWrapper}
            >
              {map(users, (item) => (
                <UserRow
                  key={item.id}
                  item={item}
                  onDelete={setUserToDelete}
                  pendingRoleChanges={pendingRoleChanges}
                  confirmedRoles={confirmedRoles}
                  onRoleChangeRequest={handleRoleChangeRequest}
                />
              ))}
            </InfiniteScroll>
          ) : (
            <div className={styles.noUsers}>
              <NoResultsFound msg="No users found!" src={emptyUsersImg} />
            </div>
          )}
        </div>
      </div>

      <Slider.Dialog
        width="300"
        show={Boolean(userToDelete)}
        onClose={() => { if (!isRemoving) setUserToDelete(null); }}
      >
        <Slider.Header 
          className={styles.deleteSliderHeader}
          title="remove user" message={<Text text={deleteeFullName} lines={1} tooltip={{ placement: "bottom" }}/>} />
        <Slider.Body>
          <p className={styles.description}>
            Are you sure you would like to remove this user from the storyboard?
          </p>
          {userToDelete && (
            <div className={styles.deleteUserCard}>
              <Avatar user={userToDelete.user} />
              <div className={styles.deleteUserInfo}>
                {deleteeFullName && (
                  <Text text={deleteeFullName} lines={1} tooltip={{ placement: "top" }} className={styles.deleteUserName} />
                )}
                <Text text={userToDelete.user.email} lines={1} tooltip={{ placement: "top" }} className={styles.deleteUserEmail} />
              </div>
            </div>
          )}
        </Slider.Body>
        <Slider.Actions>
          <Button
            variant="primary"
            icon="paperflite-tick"
            text={isRemoving ? "Removing..." : "Confirm"}
            disabled={isRemoving}
            onClick={handleConfirmDelete}
          />
          <Button
            variant="secondary"
            text="Cancel"
            disabled={isRemoving}
            onClick={() => setUserToDelete(null)}
          />
        </Slider.Actions>
      </Slider.Dialog>

      {showInviteDialog && (
        <InviteUsersDialog
          show={true}
          storyboardId={storyboardId ?? ""}
          workspaceId={workspaceId ?? ""}
          onClose={() => setShowInviteDialog(false)}
          onSuccess={() => setShowInviteDialog(false)}
        />
      )}
    </div>
  );
};

export default RolesAndPermissions;